import { useMemo, type ReactNode } from 'react';
import { format, parseISO, startOfWeek } from 'date-fns';
import { useReportingSummary } from './useReportingSummary';
import { EmptyState } from '../components/ui';
import { cn } from '../lib/utils';

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
    return (
        <div className="border border-graphite-200 dark:border-graphite-800 bg-white dark:bg-graphite-900 rounded shadow-card-md p-4">
            <div className="mb-4">
                <h2 className="font-display font-semibold text-graphite-900 dark:text-graphite-50">{title}</h2>
                {subtitle ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-500 mt-0.5">{subtitle}</p>
                ) : null}
            </div>
            {children}
        </div>
    );
}

function BarList({ items }: { items: { name: string; count: number }[] }) {
    const max = items.reduce((m, it) => Math.max(m, it.count), 0);
    if (items.length === 0) {
        return <p className="font-mono text-xs text-graphite-500">No data.</p>;
    }
    return (
        <div className="space-y-2">
            {items.map((it) => {
                const pct = max > 0 ? Math.round((it.count / max) * 100) : 0;
                return (
                    <div key={it.name} className="flex items-center gap-3">
                        <div className="w-32 shrink-0 truncate text-sm text-graphite-700 dark:text-graphite-300" title={it.name}>
                            {it.name}
                        </div>
                        <div className="flex-1 h-5 bg-graphite-100 dark:bg-graphite-800 rounded overflow-hidden">
                            <div className="h-full bg-signal-500 rounded" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-10 text-right tabular text-sm text-graphite-900 dark:text-graphite-100">{it.count}</div>
                    </div>
                );
            })}
        </div>
    );
}

function Tile({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
    return (
        <div className={cn(
            'border rounded p-3',
            alert ? 'border-amber-500/40 bg-amber-500/10' : 'border-graphite-200 dark:border-graphite-800 bg-white dark:bg-graphite-900'
        )}>
            <div className={cn('tabular font-display font-bold text-2xl', alert ? 'text-amber-600 dark:text-amber-400' : 'text-graphite-900 dark:text-graphite-50')}>
                {value}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-500 mt-1">{label}</div>
        </div>
    );
}

export function ReportsPage() {
    const { data, isLoading, error } = useReportingSummary();
    const thisWeek = useMemo(() => format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'), []);

    if (isLoading) {
        return (
            <div className="px-4 md:px-6 py-10 font-mono text-xs text-graphite-500 uppercase tracking-widest">
                Loading reports...
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="m-6 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                Error loading reports{error ? `: ${(error as Error).message}` : ''}.
            </div>
        );
    }

    const builders = [...data.builders].sort((a, b) => b.count - a.count);
    const stages = [...data.stages].sort((a, b) => a.sequence_order - b.sequence_order);
    const crew = [...data.crew_load].sort((a, b) => b.count - a.count);
    const weeks = data.pours_by_week;
    const weekMax = weeks.reduce((m, w) => Math.max(m, w.count), 0);

    return (
        <div className="px-4 md:px-6 py-6 space-y-6 max-w-6xl">
            <div className="flex items-baseline justify-between gap-4">
                <div>
                    <h1 className="font-display font-bold text-xl text-graphite-900 dark:text-graphite-50">Reports</h1>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-500 mt-1">
                        Scheduled work and book of work
                    </p>
                </div>
                <span className="font-mono text-[10px] text-graphite-500">
                    Updated {format(parseISO(data.generated_at), 'M/d h:mm a')}
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="Active jobs" value={data.tiles.active_jobs} />
                <Tile label="Scheduled items" value={data.tiles.scheduled_total} />
                <Tile label="Flatwork active" value={data.tiles.flatwork_active} />
                <Tile label="Needs review" value={data.tiles.needs_review} alert={data.tiles.needs_review > 0} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="Scheduled pours by week" subtitle="Pour stage, scheduled dates">
                    {weekMax === 0 ? (
                        <EmptyState title="No pours scheduled in this window" />
                    ) : (
                        <div className="flex items-end gap-1.5 pt-2">
                            {weeks.map((w) => {
                                const pct = weekMax > 0 ? Math.round((w.count / weekMax) * 100) : 0;
                                const isThisWeek = w.week_start === thisWeek;
                                return (
                                    <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                                        <div className="tabular text-[10px] text-graphite-500 h-3">{w.count > 0 ? w.count : ''}</div>
                                        <div className="w-full h-28 flex items-end bg-graphite-100 dark:bg-graphite-800 rounded-t">
                                            <div
                                                className={cn('w-full rounded-t', isThisWeek ? 'bg-signal-600' : 'bg-signal-500')}
                                                style={{ height: `${pct}%` }}
                                            />
                                        </div>
                                        <div className={cn('font-mono text-[9px] whitespace-nowrap', isThisWeek ? 'text-signal-600 dark:text-signal-400' : 'text-graphite-500')}>
                                            {format(parseISO(w.week_start), 'M/d')}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>

                <Card title="Crew load" subtitle="Upcoming scheduled stages">
                    <BarList items={crew} />
                </Card>

                <Card title="Book of work by builder" subtitle="Active jobs">
                    <BarList items={builders} />
                </Card>

                <Card title="Scheduled work by stage" subtitle="Stages with a scheduled date">
                    <BarList items={stages.map((s) => ({ name: s.name, count: s.count }))} />
                </Card>
            </div>
        </div>
    );
}
